import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SrIcon } from "@/components/ui/SrIcon";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { EquipmentFlagGate } from "@/components/equipment/EquipmentFlagGate";
import { ALL_DEPARTMENTS, DepartmentToggle } from "@/components/equipment/DepartmentToggle";
import { InsuranceTable, type InsuranceRow } from "@/components/equipment/InsuranceTable";
import { useDepartments } from "@/hooks/use-departments";
import { useEquipmentAccess, useEquipmentCategories, useEquipmentList } from "@/hooks/use-equipment";
import { useUpdateEquipment, type EquipmentPatch } from "@/hooks/use-equipment-mutations";
import { insuranceState, renewalDate, todayIso } from "@/lib/equipment/insurance";
import { formatDate } from "@/lib/format";

type View = "expiring" | "not_insured" | "all";

/**
 * /utstyr/forsikring (VIB-2917, skjerm D). Erstatter gjennomgangskøen i
 * verktøyet. Bare for admin og utstyrsansvarlige, fordi den viser forsikringsverdi.
 */
export default function EquipmentInsurancePage() {
  return (
    <EquipmentFlagGate>
      <EquipmentInsurance />
    </EquipmentFlagGate>
  );
}

function EquipmentInsurance() {
  const access = useEquipmentAccess();
  const { data: items = [], isLoading } = useEquipmentList(true, access.canManage);
  const { data: categories = [] } = useEquipmentCategories();
  const { data: departments = [] } = useDepartments();
  const update = useUpdateEquipment();
  const [view, setView] = useState<View>("expiring");
  const [department, setDepartment] = useState(ALL_DEPARTMENTS);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const today = todayIso();
  const bulkDate = renewalDate(null, today);

  const scoped = useMemo<InsuranceRow[]>(
    () =>
      items
        .filter((i) => department === ALL_DEPARTMENTS || i.department_id === department)
        .map((item) => ({ item, state: insuranceState(item, today) })),
    [items, department, today],
  );
  const counts = {
    expiring: scoped.filter((r) => r.state === "expiring").length,
    not_insured: scoped.filter((r) => r.state === "not_insured").length,
  };
  const rows = view === "all" ? scoped : scoped.filter((r) => r.state === view);
  const selectedInView = rows.filter((r) => selected.has(r.item.id)).map((r) => r.item.id);
  const categoryNames = new Map(categories.map((c) => [c.id, c.name]));
  const departmentNames = new Map(departments.map((d) => [d.id, d.name]));

  const patch = (ids: string[], p: EquipmentPatch, message: string) =>
    update.mutate(
      { ids, patch: p },
      {
        onSuccess: () => {
          toast.success(message);
          setSelected((s) => new Set([...s].filter((id) => !ids.includes(id))));
        },
        onError: (e) => toast.error((e as Error).message),
      },
    );

  const toggle = (id: string) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAll = () =>
    setSelected((s) => (rows.every((r) => s.has(r.item.id)) ? new Set() : new Set(rows.map((r) => r.item.id))));

  if (access.isLoading || isLoading) {
    return (
      <div className="page-container flex justify-center py-12">
        <SrIcon name="loader2" className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!access.canManage) {
    return (
      <div className="page-container animate-fade-in space-y-2">
        <h1 className="page-title">Forsikring</h1>
        <p className="text-muted-foreground">Forsikringsoversikten er for administratorer og utstyrsansvarlige.</p>
      </div>
    );
  }

  return (
    <div className="page-container animate-fade-in space-y-6">
      <nav aria-label="Brødsmuler" className="flex gap-1.5 text-sm text-muted-foreground">
        <Link to="/utstyr" className="hover:underline">Utstyr</Link>
        <span>/</span>
        <span>Forsikring</span>
      </nav>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display font-semibold text-[clamp(28px,3vw,40px)] tracking-[-0.01em] flex items-center gap-2">
          <SrIcon name="clipboard-check" className="h-6 w-6" />
          Forsikring
        </h1>
        <Button
          disabled={selectedInView.length === 0 || update.isPending}
          onClick={() =>
            patch(
              selectedInView,
              { insured_until: bulkDate, insurance_declined: false },
              `${selectedInView.length} fornyet til ${formatDate(bulkDate)}`,
            )
          }
        >
          <SrIcon name="check" className="h-4 w-4" />
          Forny valgte til {formatDate(bulkDate)}
        </Button>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <ToggleGroup type="single" variant="outline" size="sm" value={view} onValueChange={(v) => v && setView(v as View)} aria-label="Visning">
              <ToggleGroupItem value="expiring">Utløper innen 90 dager · {counts.expiring}</ToggleGroupItem>
              <ToggleGroupItem value="not_insured">Ikke forsikret · {counts.not_insured}</ToggleGroupItem>
              <ToggleGroupItem value="all">Alle</ToggleGroupItem>
            </ToggleGroup>
            <DepartmentToggle departments={departments} value={department} onChange={setDepartment} />
          </div>
          <InsuranceTable
            rows={rows}
            today={today}
            selected={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
            categoryName={(id) => categoryNames.get(id) ?? "–"}
            departmentName={(id) => (id ? (departmentNames.get(id) ?? "–") : "Ukjent")}
            disabled={update.isPending}
            onPatch={patch}
          />
          <p className="text-xs text-muted-foreground">
            Forsikringsåret følger kalenderåret. «Ikke forny» beholder utstyret, men tar det ut av listene her. Solgt og utrangert utstyr er ikke med.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
